import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import { Product } from '@setvi/shared/interfaces';
import {
  AnalyticsModules,
  useAnalytics,
  useMixpanel,
  useSelected
} from '@setvi/shared/hooks';
import {
  getProductsByImageUrl,
  getProductsByManufacturer,
  getProductsByText,
  getProductsByUrl
} from 'Services';
import { SearchFormOptions } from '@setvi/shared/components/intelligence/product-match/enums';
import { ProductMatchValues } from '@setvi/shared/components/intelligence/product-match';
import { Customer } from 'Services/Mongo/Types';
import {
  filterByCost,
  filterByManufacturer,
  filterByStock
} from './filter-functions';
import { appendPricingToProducts } from '../utils';
import { useProductsPrices } from '../api';

export enum ProductFilterTypes {
  Manufacturer = 'manufacturer',
  Stock = 'stock',
  Cost = 'cost'
}

export interface ProductFilterInterface {
  manufacturer: string[];
  stock: boolean | null;
  cost: [number, number] | [];
}

interface ResultProps {
  showStockFilter?: boolean;
  selectedCustomer?: Customer;
}

export interface AISearch {
  value: string;
  [key: string]: string | boolean;
}

export const useAISearchResults = ({
  showStockFilter,
  selectedCustomer
}: ResultProps) => {
  const [controller, setController] = useState(null);
  const { getPrices } = useProductsPrices();
  const { captureAnalytics } = useAnalytics();
  const { track } = useMixpanel();

  const [aiSearch, setAISearch] = useState<AISearch>({
    value: ''
  });
  const [aiProducts, setAiProducts] = useState<Product[]>([]);
  const [urlDescription, setURLDescription] = useState('');
  const [selectedFilters, setSelectedFilters] =
    useState<ProductFilterInterface>({
      [ProductFilterTypes.Manufacturer]: [],
      [ProductFilterTypes.Stock]: null,
      [ProductFilterTypes.Cost]: []
    });
  const [aiType, setAIType] = useState<SearchFormOptions>();
  const [loading, setLoading] = useState(false);
  // This is used to store the statusId of the SIGNALR connection on  AI Product Match
  const [statusId, setStatusId] = useState('');

  const { selected, handleSelect } = useSelected<Product>({
    dataKeyGetter: product => product._id
  });

  const { mutateAsync: fetchByUrl, isLoading: isLoadingByUrl } = useMutation(
    getProductsByUrl(controller?.signal)
  );

  const { mutateAsync: fetchByText, isLoading: isLoadingByText } = useMutation(
    getProductsByText(controller?.signal)
  );

  const { mutateAsync: fetchByImageUrl, isLoading: isLoadingByImageUrl } =
    useMutation(getProductsByImageUrl(controller?.signal));

  const {
    mutateAsync: fetchByManufacturer,
    isLoading: isLoadingByManufacturer
  } = useMutation(getProductsByManufacturer(controller?.signal));

  const apiLoading = useMemo(() => {
    switch (aiType) {
      case SearchFormOptions.text:
        return isLoadingByText;
      case SearchFormOptions.image:
        return isLoadingByImageUrl;
      case SearchFormOptions.competitor:
        return isLoadingByManufacturer;
      default:
        return isLoadingByUrl;
    }
  }, [
    aiType,
    isLoadingByText,
    isLoadingByImageUrl,
    isLoadingByUrl,
    isLoadingByManufacturer
  ]);

  const handleAISearch = async (type: SearchFormOptions, search: AISearch) => {
    const { value, ...rest } = search;
    const abortController = new AbortController();
    setController(abortController);
    const newStatusId = uuidv4();

    setAIType(type);
    setAiProducts([]);
    setAISearch(search);
    setLoading(true);
    if (selected?.length) handleSelect(false, selected);

    const body = {
      // ...rankings,
      [ProductMatchValues.Description]: '',
      [ProductMatchValues.Text]: '',
      [ProductMatchValues.Image]: '',
      [ProductMatchValues.Url]: '',
      [ProductMatchValues.ProductName]: '',
      [ProductMatchValues.ManufacturerName]: '',
      [ProductMatchValues.ManufacturerSku]: '',
      statusId: newStatusId,
      ...rest
    };

    const apis = {
      [SearchFormOptions.url]: fetchByUrl,
      [SearchFormOptions.text]: fetchByText,
      [SearchFormOptions.equipment]: fetchByText,
      [SearchFormOptions.image]: fetchByImageUrl,
      [SearchFormOptions.competitor]: fetchByManufacturer
    };

    const cleanedBody = Object.fromEntries(
      Object.entries(body).filter(([_, v]) => v !== '')
    );

    track({
      name: 'AI Tools',
      props: {
        type,
        value: search?.value || '',
        body: cleanedBody
      }
    });

    const currApi = apis[type];
    const start = Date.now();
    let durationInSeconds = (Date.now() - start) / 1000;

    setStatusId(newStatusId);
    try {
      // Remove empty values from body

      const { isSuccess, result, Message } = await currApi(cleanedBody as any);
      durationInSeconds = (Date.now() - start) / 1000;

      if (isSuccess && result) {
        const { matchedItems } = result;
        const items = matchedItems.flatMap(
          ({
            matchedInternalProducts
          }: {
            matchedInternalProducts: Product[];
          }) => matchedInternalProducts
        );

        if (items?.length) {
          // This will be used when we add customer input
          // if (isSinger) {
          //   setLoading(true);
          //   handlePrices(items);
          // } else setProducts(items);

          if (selectedCustomer?.accountNumber) {
            const prices = await getPrices({
              accountNumber: selectedCustomer.accountNumber,
              productList: items
            });

            const newItems = appendPricingToProducts(items, prices);
            setAiProducts(newItems);
          } else {
            setAiProducts(items);
          }
          setURLDescription(result?.matchedItems?.[0]?.description?.trim());
        }

        setLoading(false);
      }

      captureAnalytics(
        {
          type,
          query: search?.value,
          ...(!!Message && { error: Message }),
          totalResults:
            result?.matchedItems?.[0]?.matchedInternalProducts?.length || 0,
          ...(!!durationInSeconds && { processingTime: durationInSeconds }),
          ...(type === SearchFormOptions.image && {
            query: body?.[ProductMatchValues.Description],
            filestackUrl: body?.[ProductMatchValues.Image]
          }),
          ...(type === SearchFormOptions.competitor && {
            query: `${body?.[ProductMatchValues.ProductName]} / ${body?.[ProductMatchValues.ManufacturerName]} (${body?.[ProductMatchValues.ManufacturerSku]})`
          })
        },
        AnalyticsModules.ProductMatch
      );
    } catch (e) {
      const msg = e?.response?.data?.Message || 'Something went wrong';
      captureAnalytics(
        {
          type,
          query: search?.value,
          ...(!!msg && { error: msg }),
          totalResults: 0,
          ...(!!durationInSeconds && { processingTime: durationInSeconds }),
          ...(type === SearchFormOptions.image && {
            query: body?.[ProductMatchValues.Description],
            filestackUrl: body?.[ProductMatchValues.Image]
          }),
          ...(type === SearchFormOptions.competitor && {
            query: `${body?.[ProductMatchValues.ProductName]} / ${body?.[ProductMatchValues.ManufacturerName]} (${body?.[ProductMatchValues.ManufacturerSku]})`
          })
        },
        AnalyticsModules.ProductMatch
      );
    } finally {
      setLoading(false);
      setStatusId('');
    }
  };

  useEffect(() => {
    if (!aiProducts?.length) return;

    const minCost = Math.floor(
      aiProducts.reduce(
        (min, item) => (item.cost < min ? item.cost : min),
        aiProducts?.[0]?.cost || 0
      )
    );
    const maxCost = Math.ceil(
      aiProducts.reduce(
        (max, item) => (item.cost > max ? item.cost : max),
        aiProducts?.[0]?.cost || 0
      )
    );

    setSelectedFilters(prev => ({
      ...prev,
      [ProductFilterTypes.Cost]: [minCost || 0, maxCost || 0]
    }));
  }, [aiProducts]);

  const aiFilteredProducts = useMemo(
    () =>
      aiProducts.filter(i =>
        [
          filterByManufacturer(selectedFilters.manufacturer, i),
          ...(showStockFilter ? [filterByStock(selectedFilters.stock, i)] : []),
          filterByCost(selectedFilters.cost, i)
        ].every(Boolean)
      ),
    [aiProducts, selectedFilters, showStockFilter]
  );

  useEffect(
    () => () => {
      controller?.abort();
    },
    [controller]
  );

  return {
    aiSearchLoading: apiLoading || loading,
    aiProducts,
    aiFilteredProducts,
    aiSearch,
    aiType,
    selectedFilters,
    urlDescription,
    controller,
    statusId,
    setAIType,
    setAiProducts,
    setController,
    setAISearch,
    handleFilter: setSelectedFilters,
    handleAISearch
  };
};
